// Request checks and the E2b sample value (spec §2.3). Pure, no I/O.
import type { FieldError, Sample, ValueMap } from "./api";
import {
  DATE_FORMATS, ENUMS, NUMBER_FORMATS, normalizeDocNo, normalizeEnum, normalizeUnit, parseDate, parseNumber, parseTime,
  suggestDateFormat, suggestNumberFormat, type EnumField,
} from "./normalize";
import { SOURCE_TYPES, type Profile, type SourceType } from "./rows";
import { fieldsOf, sourceTypeText } from "./sources";

const isObj = (v: unknown): v is Record<string, unknown> => typeof v === "object" && v !== null && !Array.isArray(v);

/**
 * `format` ({ numberFormat?, dateFormat? }) and `valueMap` checked against NUMBER_FORMATS, DATE_FORMATS and ENUMS.
 * Used for requests and for profiles read from the database: an unknown dateFormat would make `parseDate` throw a TypeError.
 */
export function checkFormat(format: unknown, valueMap: unknown): { value: Profile; errors: FieldError[] } {
  const errors: FieldError[] = [];
  const value: Profile = {};
  if (format !== undefined) {
    if (!isObj(format)) errors.push({ field: "format", message: "Nesne olmalı." });
    else {
      for (const k of Object.keys(format)) if (k !== "numberFormat" && k !== "dateFormat") errors.push({ field: `format.${k}`, message: "Bilinmeyen alan." });
      if (format.numberFormat !== undefined) {
        if ((NUMBER_FORMATS as unknown[]).includes(format.numberFormat)) value.numberFormat = format.numberFormat as Profile["numberFormat"];
        else errors.push({ field: "format.numberFormat", message: `${NUMBER_FORMATS.join(", ")} değerlerinden biri olmalı.` });
      }
      if (format.dateFormat !== undefined) {
        if ((DATE_FORMATS as unknown[]).includes(format.dateFormat)) value.dateFormat = format.dateFormat as Profile["dateFormat"];
        else errors.push({ field: "format.dateFormat", message: `${DATE_FORMATS.join(", ")} değerlerinden biri olmalı. Eğik çizgili tarih kabul edilmez.` });
      }
    }
  }
  if (valueMap !== undefined) {
    if (!isObj(valueMap)) errors.push({ field: "valueMap", message: "Nesne olmalı." });
    else {
      const out: ValueMap = {};
      for (const [field, m] of Object.entries(valueMap)) {
        if (!(field in ENUMS)) { errors.push({ field: `valueMap.${field}`, message: "Kategorik alan değil." }); continue; }
        if (!isObj(m)) { errors.push({ field: `valueMap.${field}`, message: "Nesne olmalı." }); continue; }
        const targets = Object.values(ENUMS[field as EnumField]) as string[];
        const clean: Record<string, string> = {};
        for (const [raw, target] of Object.entries(m)) {
          if (typeof target === "string" && targets.includes(target) && raw.trim()) clean[raw] = target;
          else errors.push({ field: `valueMap.${field}.${raw}`, message: `Hedef ${targets.join(", ")} değerlerinden biri olmalı.` });
        }
        out[field as EnumField] = clean;
      }
      value.valueMap = out;
    }
  }
  return { value, errors };
}

/** Field names must belong to the type, columns must exist in `headers` and serve one field each. */
export function checkMapping(sourceType: SourceType, mapping: unknown, headers: string[]): { value: Record<string, string>; errors: FieldError[] } {
  const errors: FieldError[] = [];
  const value: Record<string, string> = {};
  if (!isObj(mapping)) return { value, errors: [{ field: "mapping", message: "Nesne olmalı." }] };
  const allowed = fieldsOf(sourceType);
  const seen = new Map<string, string>();
  for (const [f, col] of Object.entries(mapping)) {
    if (!allowed.includes(f)) errors.push({ field: `mapping.${f}`, message: `${sourceTypeText(sourceType)} kaynak tipinde bu alan yok.` });
    else if (typeof col !== "string" || !headers.includes(col)) errors.push({ field: `mapping.${f}`, message: "Kolon dosyada yok." });
    else if (seen.has(col)) errors.push({ field: `mapping.${f}`, message: `Aynı kolon ${seen.get(col)} alanına da eşlenmiş.` });
    else { seen.set(col, f); value[f] = col; }
  }
  return { value, errors };
}

const pad = (n: number) => String(n).padStart(2, "0");
/** A cell value as the user sees it: dates `YYYY-AA-GG`, times `SS:DD` (Excel times are 1899-12-30 based). */
function showRaw(v: unknown): string | number {
  if (v instanceof Date) {
    return v.getUTCFullYear() < 1901 ? `${pad(v.getUTCHours())}:${pad(v.getUTCMinutes())}` : v.toISOString().slice(0, 10);
  }
  return typeof v === "number" ? v : String(v);
}

/** First filled value of the column, raw and normalized the way validateRows would (spec §2.3). `column` values are `cellValue`s. */
export function sampleOf(sourceType: SourceType, field: string, column: string, values: unknown[], profile: Profile): Sample {
  const v = values.find((x) => x !== null && x !== undefined);
  if (v === undefined) return { field, column, raw: null, normalized: null };
  const raw = showRaw(v);
  const fail = (problem: string): Sample => ({ field, column, raw, normalized: null, problem });
  const kind = (SOURCE_TYPES[sourceType].kinds as Record<string, string>)[field]!;
  const done = (r: { ok: true; value: string | number } | { ok: false; reason: string }): Sample =>
    r.ok ? { field, column, raw, normalized: r.value } : fail(r.reason);
  switch (kind) {
    case "text": return typeof v === "string" || typeof v === "number" ? { field, column, raw, normalized: String(v).trim() } : fail("INVALID_TEXT");
    case "docNo": return done(normalizeDocNo(v));
    case "number": {
      let fmt = profile.numberFormat;
      if (!fmt) { const s = suggestNumberFormat(values); if (s.status === "ambiguous") return fail("AMBIGUOUS_FORMAT"); fmt = s.status === "ok" ? s.format : "plain"; }
      return done(parseNumber(v, fmt));
    }
    case "date": return done(parseDate(v, profile.dateFormat ?? (() => { const s = suggestDateFormat(values); return s.status === "ok" ? s.format : null; })()));
    case "time": return done(parseTime(v));
    case "unit": { const r = normalizeUnit(v); return r.ok ? { field, column, raw, normalized: r.value.unit } : fail(r.reason); }
    default: return done(normalizeEnum(kind as EnumField, v, profile.valueMap?.[kind as EnumField]));
  }
}


/** The one label of a version's source: the file name, or "Elle giriş #N" for a manual entry (ADR-0016). */
export const uploadLabel = (u: { id: number; origin: "file" | "manual"; fileName: string | null }) =>
  u.origin === "manual" ? `Elle giriş #${u.id}` : u.fileName ?? "";

/** The label of a version (ADR-0016, ui.md §11.5): "Yükleme #N" for a file, "Elle giriş #N" for a manual entry. No screen writes the prefix itself. */
export const versionLabel = (origin: "file" | "manual", uploadId: number) => `${origin === "manual" ? "Elle giriş" : "Yükleme"} #${uploadId}`;
